import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Divider, Grid, Group, Text, TextInput, UnstyledButton} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import AntibodyChainLegend from 'antibodies/antibody-map/AntibodyChainLegend';
import * as AntibodyFormatDAO from 'antibodies/format/AntibodyFormatDAO';
import {VeritasHelpGuideModal} from 'antibodies/format/help-modal/VeritasHelpGuideModal';
import {useVeritasFormatPreviewQuery} from 'antibodies/format/useVeritasFormatPreviewQuery';
import Antibody from 'antibodies/visualization/Antibody';
import BenchlingConfig from 'BenchlingConfig';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {FONT_PROXIMA_12, FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import legacyRunModal from 'ui-library/legacyRunModal';
import {CenteredSpinner} from 'ui-library/Spinner';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

export interface AntibodyFormatCreationPaneProps {
  onRequestClose: () => void;
  onCreate?: (formatId: string) => Promise<void>;
  analyticsMetadata: AnalyticsMetadataForContext;
  /** When provided, the pane opens in edit mode with fields pre-populated and VERITAS name read-only. */
  existingFormat?: AntibodyFormatGraphQL;
  /** Called after a successful display name update. */
  onDisplayNameUpdated?: () => Promise<void>;
}

const EXAMPLE_FORMATS = ['Fab-Fc', 'IgG-scFab', '[Fd:LC*scFv]-heteroCH3'];

export default function AntibodyFormatCreationPane({
  onRequestClose,
  onCreate,
  analyticsMetadata,
  existingFormat,
  onDisplayNameUpdated,
}: AntibodyFormatCreationPaneProps): JSX.Element {
  const isEditMode = existingFormat != null;
  const [formatName, setFormatName] = useState(isEditMode ? existingFormat.name : '');
  const [veritasName, setVeritasName] = useState(
    isEditMode ? (existingFormat.originalVeritas ?? existingFormat.name) : ''
  );
  const [previewName, setPreviewName] = useState<string | null>(
    isEditMode ? (existingFormat.originalVeritas ?? existingFormat.name) : null
  );
  const [isSaving, setIsSaving] = useState(false);

  const {status: previewStatus, result: previewResult} = useVeritasFormatPreviewQuery(previewName);

  const handleCreate = useCallback(async (): Promise<void> => {
    setIsSaving(true);

    try {
      const result = await AntibodyFormatDAO.createAntibodyFormat(
        {
          veritasName: veritasName.trim(),
          ...(formatName.trim() && {displayName: formatName.trim()}),
        },
        analyticsMetadata
      );
      await onCreate?.(result.antibodyFormatId);
    } finally {
      setIsSaving(false);
    }
  }, [veritasName, formatName, onCreate, analyticsMetadata]);

  const handleSaveDisplayName = useCallback(async (): Promise<void> => {
    if (existingFormat == null) {
      return;
    }
    setIsSaving(true);
    try {
      await AntibodyFormatDAO.updateAntibodyFormatDisplayName(existingFormat.id, formatName.trim());
      notifications.success('Display name updated successfully.');
      await onDisplayNameUpdated?.();
      onRequestClose();
    } catch (e) {
      notifications.error('Failed to update display name.');
      throw e;
    } finally {
      setIsSaving(false);
    }
  }, [existingFormat, formatName, onDisplayNameUpdated, onRequestClose]);

  const handlePreview = (): void => {
    setPreviewName(veritasName.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && veritasName.trim().length > 0) {
      handlePreview();
    }
  };

  const handleExampleFormatClick = (name: string): void => {
    setVeritasName(name);
    setPreviewName(name);
  };

  const handleClear = (): void => {
    setVeritasName('');
    setPreviewName(null);
  };

  const handleOpenHelpGuide = (): void => {
    legacyRunModal(VeritasHelpGuideModal, {});
  };

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'AntibodyFormatCreationPane', element: 'root'})}
    >
      <div className={css(styles.header)}>
        <div className={css(styles.headerTitle)}>
          {isEditMode ? 'Edit display name' : 'Create new antibody format'}
        </div>
        <div className={css(styles.headerButtons)}>
          <Button
            onClick={onRequestClose}
            text="Cancel"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
          />
          <Button
            onClick={isEditMode ? handleSaveDisplayName : handleCreate}
            text={isSaving ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save' : 'Create'}
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            disabled={
              isEditMode
                ? isSaving || formatName.trim().length === 0
                : !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES ||
                  veritasName.trim().length === 0 ||
                  isSaving
            }
            dataTestAttrs={{
              component: 'AntibodyFormatCreationPane',
              element: isEditMode ? 'saveButton' : 'createButton',
            }}
          />
        </div>
      </div>
      <Divider />
      <Grid gutter="md">
        <Grid.Col span={6} p="xl">
          <Text className={css(styles.veritasHeader)}>
            {isEditMode
              ? 'Update the display name for this format'
              : 'Use VERITAS nomenclature to define a custom format'}
          </Text>
          <FormGroup>
            <Group align="center">
              <FieldLabel
                label="VERITAS name"
                htmlFor="veritas-name"
                isRequired={!isEditMode}
                className={css(styles.veritasNameLabel)}
              />
              {!isEditMode && (
                <UnstyledButton onClick={handleOpenHelpGuide} className={css(styles.veritasModalButton)}>
                  <BenchlingIcon icon="more-info" />
                  <span>How to use VERITAS</span>
                </UnstyledButton>
              )}
            </Group>
            <TextInput
              size="md"
              id="veritas-name"
              onChange={
                isEditMode
                  ? undefined
                  : (e) => {
                      setVeritasName(e.target.value);
                    }
              }
              onKeyDown={isEditMode ? undefined : handleKeyPress}
              placeholder="Enter VERITAS name"
              value={veritasName}
              readOnly={isEditMode}
              disabled={isEditMode}
              onBlur={
                isEditMode
                  ? undefined
                  : () => {
                      analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.TYPED_CUSTOM_FORMAT_VERITAS_NAME, {
                        ...analyticsMetadata,
                        veritas_name: veritasName,
                      });
                    }
              }
            />
            {!isEditMode && (
              <div className={css(styles.exampleFormatsSection)}>
                <span>Examples:</span>
                {EXAMPLE_FORMATS.map((format) => (
                  <Button
                    key={format}
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    size={ButtonSize.MINI}
                    text={format}
                    textColor={Color.GRAY_700}
                    onClick={() => {
                      handleExampleFormatClick(format);
                    }}
                    className={css(styles.exampleFormatButton)}
                  />
                ))}
              </div>
            )}
            <FieldLabel label="Display name" htmlFor="display-name" />
            <TextInput
              size="md"
              id="display-name"
              onChange={(e) => {
                setFormatName(e.target.value);
              }}
              placeholder="Enter name"
              value={formatName}
              onBlur={() => {
                analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.TYPED_CUSTOM_FORMAT_DISPLAY_NAME, {
                  ...analyticsMetadata,
                  display_name: formatName,
                });
              }}
            />
            {!isEditMode && (
              <div className={css(styles.previewButtons)}>
                <Button
                  onClick={handleClear}
                  icon="close"
                  text="Clear"
                  buttonStyle={ButtonStyle.SECONDARY_WHITE}
                  size={ButtonSize.SMALL}
                  disabled={veritasName.trim().length === 0}
                />
                <Button
                  onClick={handlePreview}
                  icon="eye"
                  text="Preview"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  disabled={veritasName.trim().length === 0}
                  dataTestAttrs={{component: 'AntibodyFormatCreationPane', element: 'previewButton'}}
                />
              </div>
            )}
          </FormGroup>
        </Grid.Col>
        <Grid.Col span={6} p="xl">
          <div className={css(styles.previewPanel)}>
            {previewName == null ? (
              <div className={css(styles.previewPlaceholder)}>
                Enter VERITAS name to preview antibody format
              </div>
            ) : (
              <>
                {previewStatus === QueryResultType.PENDING && <CenteredSpinner />}
                {previewStatus === QueryResultType.ERROR && (
                  <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to preview format." />
                )}
                {previewStatus === QueryResultType.SUCCESS &&
                  previewResult != null &&
                  previewResult.__typename === 'VeritasFormatPreviewErrorNode' && (
                    <InlineBanner
                      bannerType={InlineBannerType.ERROR}
                      text={`Unable to preview format: ${previewResult.message}`}
                    />
                  )}
                {previewStatus === QueryResultType.SUCCESS &&
                  previewResult != null &&
                  previewResult.__typename === 'AntibodyFormatNode' && (
                    <div
                      {...getTestProps({
                        component: 'AntibodyFormatCreationPane',
                        element: 'preview-container',
                      })}
                    >
                      <div className={css(styles.chainLegendWrapper)}>
                        <AntibodyChainLegend antibodyFormat={previewResult} />
                      </div>
                      <Antibody format={previewResult} showLabels={true} showPopovers={true} />
                    </div>
                  )}
              </>
            )}
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    height: '52px',
  },
  headerTitle: {
    ...FONT_PROXIMA_16,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  headerButtons: {
    display: 'flex',
    gap: '12px',
  },
  veritasHeader: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_900,
    marginBottom: '12px',
  },
  veritasNameLabel: {
    flexGrow: 1,
  },
  veritasModalButton: {
    color: Color.BLUE_400,
    fontWeight: 'bold',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginBottom: '5px',
    ':hover': {
      color: Color.BLUE_500,
    },
  },
  exampleFormatsSection: {
    ...FONT_PROXIMA_12,
    display: 'flex',
    alignItems: 'center',
    gap: '4px 8px',
    flexWrap: 'wrap',
    marginTop: '4px',
    marginBottom: '12px',
  },
  exampleFormatButton: {
    backgroundColor: Color.GRAY_200,
  },
  previewButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    justifyContent: 'flex-end',
  },
  previewPanel: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    backgroundColor: Color.GRAY_50,
    padding: '8px',
    minHeight: '304px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewPlaceholder: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_600,
    textAlign: 'center',
  },
  chainLegendWrapper: {
    backgroundColor: Color.WHITE,
    ...FONT_PROXIMA_12,
  },
});
